import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { fmtCurrency } from '../lib/localeUtils';
import {
  Calculator, Sparkles, History, BookOpen, Loader2, ArrowRight,
  Check, X, AlertTriangle, TrendingUp, TrendingDown, Pencil,
  Trash2, Save, RefreshCw, ChevronDown, ChevronUp, Target, Zap
} from 'lucide-react';
import { keyed } from '../lib/listKeys';

export default function PricingPage() {
  const [tab, setTab] = useState('calculator');

  return (
    <div className="space-y-6" data-testid="pricing-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Product Pricing
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Calculate, analyze, and manage pricing across your product catalog
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="calculator" className="gap-1.5" data-testid="tab-calculator">
            <Calculator className="h-3.5 w-3.5" /> Calculator
          </TabsTrigger>
          <TabsTrigger value="ai" className="gap-1.5" data-testid="tab-ai">
            <Sparkles className="h-3.5 w-3.5" /> AI Analysis
          </TabsTrigger>
          <TabsTrigger value="rules" className="gap-1.5" data-testid="tab-rules">
            <BookOpen className="h-3.5 w-3.5" /> Rules
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-1.5" data-testid="tab-history">
            <History className="h-3.5 w-3.5" /> History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="calculator"><PriceCalculator /></TabsContent>
        <TabsContent value="ai"><AIAnalysis /></TabsContent>
        <TabsContent value="rules"><PricingRules /></TabsContent>
        <TabsContent value="history"><PriceHistory /></TabsContent>
      </Tabs>
    </div>
  );
}


// ==================== PRICE CALCULATOR ====================

const MODE_CONFIG = {
  margin:      { label: 'Margin %',     unit: '%',   desc: 'Price = Base / (1 - %)',      needsPct: true },
  markup:      { label: 'Markup %',     unit: '%',   desc: 'Price = Base x (1 + %)',       needsPct: true },
  add_to:      { label: 'Add To',       unit: '',    desc: 'Price = Base + Value',          needsPct: false },
  subtract:    { label: 'Subtract',     unit: '',    desc: 'Price = Base - Value',          needsPct: false },
  multiply_by: { label: 'Multiply By',  unit: 'x',   desc: 'Price = Base x Value',         needsPct: false },
};

function PriceCalculator() {
  const [base, setBase] = useState('cost');
  const [mode, setMode] = useState('markup');
  const [pct, setPct] = useState(30);
  const [scope, setScope] = useState('all');
  const [category, setCategory] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [rounding, setRounding] = useState('0.01');
  const [calculating, setCalculating] = useState(false);
  const [results, setResults] = useState(null);
  const [editedPrices, setEditedPrices] = useState({});
  const [applying, setApplying] = useState(false);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [rules, setRules] = useState([]);
  const [sortField, setSortField] = useState('change_pct');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    Promise.all([
      api.get('/products?limit=1000'),
      api.get('/pricing/rules'),
    ]).then(([prodsRes, rulesRes]) => {
      setProducts(prodsRes.data.products || []);
      const cats = [...new Set((prodsRes.data.products || []).map(p => p.category).filter(Boolean))];
      setCategories(cats.sort());
      setRules(rulesRes.data.rules || []);
    }).catch(() => {});
  }, []);

  const handleCalculate = async () => {
    setCalculating(true);
    setResults(null);
    setEditedPrices({});
    try {
      const body = { base, mode, value: pct, scope, rounding };
      if (scope === 'category') body.category = category;
      if (scope === 'selected') body.product_ids = selectedIds;
      const res = await api.post('/pricing/calculate', body);
      setResults(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Calculation failed');
    } finally {
      setCalculating(false);
    }
  };

  const handleApply = async () => {
    if (!results) return;
    const eligible = results.results.filter(r => !r.skipped);
    const prices = eligible.map(r => ({
      product_id: r.product_id,
      new_price: editedPrices[r.product_id] ?? r.new_price,
    }));
    setApplying(true);
    try {
      const res = await api.post('/pricing/apply', { prices });
      toast.success(`Updated ${res.data.applied} product prices`);
      if (res.data.errors?.length) {
        toast.warning(`${res.data.errors.length} products had errors`);
      }
      setResults(null);
      setEditedPrices({});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply prices');
    } finally {
      setApplying(false);
    }
  };

  const toggleSelect = (pid) => {
    setSelectedIds(prev => prev.includes(pid) ? prev.filter(id => id !== pid) : [...prev, pid]);
  };

  const handleSmartReprice = async (rule) => {
    setBase(rule.base || 'cost');
    setMode(rule.mode);
    setPct(rule.value);
    setScope('category');
    setCategory(rule.category);
    setRounding(rule.rounding || '0.01');
    setResults(null);
    setEditedPrices({});
    setCalculating(true);
    try {
      const res = await api.post('/pricing/calculate', {
        base: rule.base || 'cost', mode: rule.mode, value: rule.value, scope: 'category',
        category: rule.category, rounding: rule.rounding || '0.01',
      });
      setResults(res.data);
      const modeLabel = MODE_CONFIG[rule.mode]?.label || rule.mode;
      toast.success(`Calculated ${rule.category} — ${modeLabel} ${rule.value}${MODE_CONFIG[rule.mode]?.needsPct ? '%' : ''}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Calculation failed');
    } finally {
      setCalculating(false);
    }
  };

  const sorted = results?.results ? [...results.results].sort((a, b) => {
    const av = a[sortField] ?? 0, bv = b[sortField] ?? 0;
    return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  }) : [];

  const totalImpact = sorted.reduce((s, r) => s + (r.skipped ? 0 : ((editedPrices[r.product_id] ?? r.new_price) - r.current_price)), 0);

  const SortBtn = ({ field, children }) => (
    <button className="flex items-center gap-0.5 font-medium" onClick={() => { setSortField(field); setSortDir(d => sortField === field && d === 'desc' ? 'asc' : 'desc'); }}>
      {children}
      {sortField === field && (sortDir === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
    </button>
  );

  return (
    <div className="space-y-5 mt-4">
      {/* Smart Reprice */}
      {rules.length > 0 && (
        <Card data-testid="smart-reprice-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="h-4 w-4 text-amber-500" /> Smart Reprice
            </CardTitle>
            <CardDescription>One-click repricing using your saved category rules</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {rules.map(rule => {
                const catProducts = products.filter(p => p.category === rule.category).length;
                return (
                  <Button
                    key={rule.rule_id || rule.category}
                    variant="outline"
                    className="gap-2 h-auto py-2.5 px-4 hover:border-primary/50 hover:bg-primary/5 transition-colors"
                    onClick={() => handleSmartReprice(rule)}
                    disabled={calculating}
                    data-testid={`smart-reprice-${rule.category.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <Zap className="h-3.5 w-3.5 text-amber-500" />
                    <span className="font-medium">{rule.category}</span>
                    <Badge variant="secondary" className="text-[10px] font-mono">
                      {(rule.base || 'cost') === 'selling' ? 'SP' : 'CP'} | {MODE_CONFIG[rule.mode]?.label || rule.mode} {rule.value}{MODE_CONFIG[rule.mode]?.needsPct ? '%' : ''}
                    </Badge>
                    <span className="text-[10px] text-muted-foreground">{catProducts} items</span>
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Controls */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Calculator className="h-4 w-4 text-primary" /> Price Calculator
          </CardTitle>
          <CardDescription>Calculate selling prices based on cost or selling price using margin, markup, or arithmetic methods</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label>Base Price</Label>
              <Select value={base} onValueChange={setBase}>
                <SelectTrigger data-testid="pricing-base-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cost">Cost Price</SelectItem>
                  <SelectItem value="selling">Selling Price</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                {base === 'cost' ? 'Calculate from unit cost' : 'Calculate from current selling price'}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Pricing Method</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="pricing-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="markup">Markup %</SelectItem>
                  <SelectItem value="margin">Margin %</SelectItem>
                  <SelectItem value="add_to">Add To</SelectItem>
                  <SelectItem value="subtract">Subtract</SelectItem>
                  <SelectItem value="multiply_by">Multiply By</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                {MODE_CONFIG[mode]?.desc}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Value {MODE_CONFIG[mode]?.unit ? `(${MODE_CONFIG[mode].unit})` : ''}</Label>
              <Input
                type="number" value={pct} onChange={e => setPct(parseFloat(e.target.value) || 0)}
                min={mode === 'multiply_by' ? 0.01 : 0} max={mode === 'margin' ? 99 : undefined} step={mode === 'multiply_by' ? 0.1 : 0.5}
                data-testid="pricing-pct-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Scope</Label>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger data-testid="pricing-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Products</SelectItem>
                  <SelectItem value="category">By Category</SelectItem>
                  <SelectItem value="selected">Selected Products</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Rounding</Label>
              <Select value={rounding} onValueChange={setRounding}>
                <SelectTrigger data-testid="pricing-rounding-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Rounding</SelectItem>
                  <SelectItem value="0.01">Nearest 0.01</SelectItem>
                  <SelectItem value="0.05">Nearest 0.05</SelectItem>
                  <SelectItem value="0.10">Nearest 0.10</SelectItem>
                  <SelectItem value="0.50">Nearest 0.50</SelectItem>
                  <SelectItem value="1.00">Nearest 1.00</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {scope === 'category' && (
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="pricing-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {scope === 'selected' && (
            <div className="space-y-2">
              <Label>Select Products ({selectedIds.length} selected)</Label>
              <div className="max-h-48 overflow-y-auto border rounded-md p-2 space-y-1">
                {products.map(p => (
                  <label key={p.product_id} className="flex items-center gap-2 text-sm py-1 px-2 rounded hover:bg-muted cursor-pointer">
                    <Checkbox
                      checked={selectedIds.includes(p.product_id)}
                      onCheckedChange={() => toggleSelect(p.product_id)}
                    />
                    <span className="flex-1">{p.name}</span>
                    <span className="text-xs text-muted-foreground">{p.category}</span>
                    <span className="text-xs font-mono">{fmtCurrency(p.unit_price)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <Button onClick={handleCalculate} disabled={calculating || (scope === 'category' && !category) || (scope === 'selected' && !selectedIds.length)} className="gap-1.5" data-testid="calculate-btn">
            {calculating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />}
            Calculate Prices
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {results && (
        <Card data-testid="pricing-results">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                Price Preview — {results.total} products
                {results.skipped > 0 && <span className="text-muted-foreground font-normal text-sm ml-2">({results.skipped} skipped — no cost)</span>}
              </CardTitle>
              <div className="flex items-center gap-3 text-sm">
                <span className="text-muted-foreground">Net Impact:</span>
                <Badge variant={totalImpact >= 0 ? 'default' : 'destructive'} className="font-mono">
                  {totalImpact >= 0 ? '+' : ''}{fmtCurrency(totalImpact)}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[250px]"><SortBtn field="name">Product</SortBtn></TableHead>
                    <TableHead><SortBtn field="category">Category</SortBtn></TableHead>
                    <TableHead className="text-right"><SortBtn field="unit_cost">Cost</SortBtn></TableHead>
                    <TableHead className="text-right"><SortBtn field="current_price">Current</SortBtn></TableHead>
                    <TableHead className="text-right"><SortBtn field="new_price">New Price</SortBtn></TableHead>
                    <TableHead className="text-right"><SortBtn field="change_pct">Change</SortBtn></TableHead>
                    <TableHead className="text-right w-[100px]">Edit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map(r => {
                    const edited = editedPrices[r.product_id];
                    const displayPrice = edited ?? r.new_price;
                    const isEdited = edited !== undefined;
                    return (
                      <TableRow key={r.product_id} className={r.skipped ? 'opacity-50' : ''} data-testid={`pricing-row-${r.product_id}`}>
                        <TableCell className="font-medium text-sm">{r.name}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{r.category}</Badge></TableCell>
                        <TableCell className="text-right text-sm font-mono">{r.unit_cost > 0 ? fmtCurrency(r.unit_cost) : '—'}</TableCell>
                        <TableCell className="text-right text-sm font-mono">{fmtCurrency(r.current_price)}</TableCell>
                        <TableCell className="text-right">
                          {r.skipped ? (
                            <span className="text-xs text-muted-foreground italic">{r.reason}</span>
                          ) : (
                            <span className={`text-sm font-mono font-semibold ${isEdited ? 'text-blue-600 dark:text-blue-400' : ''}`}>
                              {fmtCurrency(displayPrice)}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {!r.skipped && (
                            <span className={`text-xs font-mono ${r.change > 0 ? 'text-emerald-600' : r.change < 0 ? 'text-red-600' : ''}`}>
                              {r.change > 0 ? '+' : ''}{r.change_pct}%
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {!r.skipped && (
                            <Input
                              type="number"
                              className="h-7 w-24 text-xs font-mono text-right ml-auto"
                              value={displayPrice}
                              onChange={e => setEditedPrices(prev => ({ ...prev, [r.product_id]: parseFloat(e.target.value) || 0 }))}
                              step={0.01}
                              min={0}
                              data-testid={`edit-price-${r.product_id}`}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
          <div className="flex justify-between items-center p-4 border-t bg-muted/30">
            <p className="text-xs text-muted-foreground">
              {Object.keys(editedPrices).length > 0 && (
                <span className="text-blue-600 font-medium">{Object.keys(editedPrices).length} manually edited. </span>
              )}
              Prices will be saved permanently when applied.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setResults(null); setEditedPrices({}); }} data-testid="discard-pricing-btn">
                <X className="h-4 w-4 mr-1.5" /> Discard
              </Button>
              <Button onClick={handleApply} disabled={applying} className="gap-1.5" data-testid="apply-pricing-btn">
                {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Apply {sorted.filter(r => !r.skipped).length} Prices
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}


// ==================== AI ANALYSIS ====================

function AIAnalysis() {
  const [scope, setScope] = useState('all');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);

  useEffect(() => {
    api.get('/products?limit=1000').then(res => {
      const cats = [...new Set((res.data.products || []).map(p => p.category).filter(Boolean))];
      setCategories(cats.sort());
    }).catch(() => {});
  }, []);

  const runAnalysis = async () => {
    setLoading(true);
    setAnalysis(null);
    try {
      const body = { scope };
      if (scope === 'category') body.category = category;
      const res = await api.post('/pricing/ai-analysis', body);
      setAnalysis(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI analysis failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5 mt-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" /> AI Price Analysis
          </CardTitle>
          <CardDescription>Get AI-powered pricing recommendations based on costs, margins, sales trends, and stock levels</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="space-y-2">
              <Label>Scope</Label>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger className="w-48" data-testid="ai-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Products</SelectItem>
                  <SelectItem value="category">By Category</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {scope === 'category' && (
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="w-48" data-testid="ai-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button onClick={runAnalysis} disabled={loading || (scope === 'category' && !category)} className="gap-1.5" data-testid="run-ai-analysis-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {loading ? 'Analyzing...' : 'Run Analysis'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading && (
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary mb-3" />
            <p className="text-sm text-muted-foreground">AI is analyzing pricing data across your catalog...</p>
          </CardContent>
        </Card>
      )}

      {analysis && (
        <div className="space-y-4">
          {/* Summary */}
          <Card>
            <CardContent className="p-5">
              <p className="text-sm leading-relaxed">{analysis.overall_assessment}</p>
              <div className="flex gap-4 mt-3 text-sm">
                <Badge variant="outline" className="gap-1"><Target className="h-3 w-3" /> Avg Margin: {analysis.avg_margin}%</Badge>
                {analysis.products_below_cost > 0 && (
                  <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" /> {analysis.products_below_cost} below cost</Badge>
                )}
                <Badge variant="outline">{analysis.products_analyzed} analyzed</Badge>
              </div>
            </CardContent>
          </Card>

          {/* Quick Wins & Risks side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {analysis.quick_wins?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-1.5"><Zap className="h-4 w-4 text-amber-500" /> Quick Wins</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {keyed(analysis.quick_wins).map(({ item: w, key }) => (
                    <div key={key} className="flex items-start gap-2 text-sm">
                      <Check className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />
                      <span>{w}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
            {analysis.pricing_risks?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-1.5"><AlertTriangle className="h-4 w-4 text-red-500" /> Pricing Risks</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {keyed(analysis.pricing_risks).map(({ item: r, key }) => (
                    <div key={key} className="flex items-start gap-2 text-sm">
                      <X className="h-3.5 w-3.5 text-red-500 mt-0.5 shrink-0" />
                      <span>{r}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Product Recommendations */}
          {analysis.recommendations?.length > 0 && (
            <Card data-testid="ai-recommendations">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Product Recommendations</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Current</TableHead>
                      <TableHead className="text-right">Suggested</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Priority</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {keyed(analysis.recommendations, rec => rec.product_id || rec.product_name).map(({ item: rec, key }) => (
                      <TableRow key={key}>
                        <TableCell className="font-medium text-sm">{rec.product_name}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{fmtCurrency(rec.current_price)}</TableCell>
                        <TableCell className="text-right font-mono text-sm font-semibold">{fmtCurrency(rec.suggested_price)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[200px]">{rec.reason}</TableCell>
                        <TableCell>
                          <Badge variant={rec.priority === 'high' ? 'destructive' : rec.priority === 'medium' ? 'default' : 'outline'} className="text-[10px]">
                            {rec.priority}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Category Insights */}
          {analysis.category_insights?.length > 0 && (
            <Card data-testid="category-insights">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Category Insights</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {keyed(analysis.category_insights, ci => ci.category).map(({ item: ci, key }) => (
                  <div key={key} className="flex items-start gap-3 p-3 rounded-lg bg-muted/40">
                    <Badge variant="outline" className="shrink-0 mt-0.5">{ci.category}</Badge>
                    <div className="text-sm flex-1">
                      <span className="font-mono text-xs mr-2">{ci.avg_margin}% margin</span>
                      <span className="text-muted-foreground">{ci.recommendation}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}


// ==================== PRICING RULES ====================

function PricingRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ category: '', mode: 'markup', value: 30, rounding: '0.01', auto_apply: false });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesRes, prodsRes] = await Promise.all([
        api.get('/pricing/rules'),
        api.get('/products?limit=1000'),
      ]);
      setRules(rulesRes.data.rules || []);
      const cats = [...new Set((prodsRes.data.products || []).map(p => p.category).filter(Boolean))];
      setCategories(cats.sort());
    } catch (err) {
      console.error('Failed to load pricing rules:', err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    if (!form.category) { toast.error('Select a category'); return; }
    setSaving(true);
    try {
      await api.post('/pricing/rules', form);
      toast.success(`Pricing rule saved for ${form.category}`);
      setFormOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (ruleId) => {
    try {
      await api.delete(`/pricing/rules/${ruleId}`);
      toast.success('Rule deleted');
      fetchData();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-5 mt-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-primary" /> Pricing Rules
              </CardTitle>
              <CardDescription>Define default pricing rules per product category with flexible calculation methods</CardDescription>
            </div>
            <Button onClick={() => { setForm({ category: '', base: 'cost', mode: 'markup', value: 30, rounding: '0.01', auto_apply: false }); setFormOpen(true); }} className="gap-1.5" size="sm" data-testid="add-rule-btn">
              <BookOpen className="h-3.5 w-3.5" /> Add Rule
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
          ) : rules.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No pricing rules yet. Define category-level rules for consistent pricing.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead>Base</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead>Rounding</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map(r => (
                  <TableRow key={r.rule_id} data-testid={`rule-row-${r.rule_id}`}>
                    <TableCell className="font-medium">{r.category}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{(r.base || 'cost') === 'selling' ? 'Selling Price' : 'Cost Price'}</Badge></TableCell>
                    <TableCell><Badge variant="outline" className="text-xs capitalize">{(MODE_CONFIG[r.mode]?.label) || r.mode}</Badge></TableCell>
                    <TableCell className="text-right font-mono">{r.value}{MODE_CONFIG[r.mode]?.needsPct ? '%' : (MODE_CONFIG[r.mode]?.unit ? ` ${MODE_CONFIG[r.mode].unit}` : '')}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.rounding === 'none' ? 'None' : `Nearest ${r.rounding}`}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.created_by}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7"
                          onClick={() => { setForm({ category: r.category, base: r.base || 'cost', mode: r.mode, value: r.value, rounding: r.rounding, auto_apply: r.auto_apply || false }); setFormOpen(true); }}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => handleDelete(r.rule_id)} data-testid={`delete-rule-${r.rule_id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-sm" data-testid="rule-dialog">
          <DialogHeader>
            <DialogTitle>Pricing Rule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="rule-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Base Price</Label>
              <Select value={form.base || 'cost'} onValueChange={v => setForm(f => ({ ...f, base: v }))}>
                <SelectTrigger data-testid="rule-base-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cost">Cost Price</SelectItem>
                  <SelectItem value="selling">Selling Price</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Method</Label>
                <Select value={form.mode} onValueChange={v => setForm(f => ({ ...f, mode: v }))}>
                  <SelectTrigger data-testid="rule-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="markup">Markup %</SelectItem>
                    <SelectItem value="margin">Margin %</SelectItem>
                    <SelectItem value="add_to">Add To</SelectItem>
                    <SelectItem value="subtract">Subtract</SelectItem>
                    <SelectItem value="multiply_by">Multiply By</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Value</Label>
                <Input type="number" value={form.value} onChange={e => setForm(f => ({ ...f, value: parseFloat(e.target.value) || 0 }))}
                  min={0} step={form.mode === 'multiply_by' ? 0.1 : 0.5} data-testid="rule-value-input" />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground px-1">
              {MODE_CONFIG[form.mode]?.desc} — based on {(form.base || 'cost') === 'selling' ? 'selling price' : 'cost price'}
            </p>
            <div className="space-y-2">
              <Label>Rounding</Label>
              <Select value={form.rounding} onValueChange={v => setForm(f => ({ ...f, rounding: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="0.01">0.01</SelectItem>
                  <SelectItem value="0.05">0.05</SelectItem>
                  <SelectItem value="0.10">0.10</SelectItem>
                  <SelectItem value="0.50">0.50</SelectItem>
                  <SelectItem value="1.00">1.00</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-rule-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
              Save Rule
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ==================== PRICE HISTORY ====================

function PriceHistory() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/pricing/history?limit=200').then(res => {
      setRecords(res.data.records || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-5 mt-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <History className="h-4 w-4 text-primary" /> Price Change History
          </CardTitle>
          <CardDescription>Audit trail of all product price changes</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
          ) : records.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <History className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No price changes recorded yet. Use the Calculator to apply bulk pricing.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Old Price</TableHead>
                  <TableHead className="text-center"></TableHead>
                  <TableHead className="text-right">New Price</TableHead>
                  <TableHead className="text-right">Change</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map(r => (
                  <TableRow key={r.record_id} data-testid={`history-row-${r.record_id}`}>
                    <TableCell className="font-medium text-sm">{r.product_name}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{r.category}</Badge></TableCell>
                    <TableCell className="text-right font-mono text-sm">{fmtCurrency(r.old_price)}</TableCell>
                    <TableCell className="text-center">
                      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground mx-auto" />
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm font-semibold">{fmtCurrency(r.new_price)}</TableCell>
                    <TableCell className="text-right">
                      <span className={`text-xs font-mono ${r.change_pct > 0 ? 'text-emerald-600' : r.change_pct < 0 ? 'text-red-600' : ''}`}>
                        {r.change_pct > 0 ? '+' : ''}{r.change_pct}%
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.changed_by}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
